import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCompletedTerminalCalls } from "../../data/completedterminalcall";
import { hideModal } from "../../data/modal";
import dayjs from "dayjs";
import { TerminalCall } from "../../types/TerminalCall";
import { RenderLabels, RenderTerminalCallLock, RenderTerminalName } from "../Helpers";
import ContainerCounts from "../partial/ContainerCounts";
import TerminalCallWeightsAvailable from "../partial/TerminalCallWeightsAvailable";

import {
  Button,
  CircularProgress,
  Grid,
  List,
  ListItem,
  Typography,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from "@mui/material";

import {
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

import {
  Close as CloseIcon,
} from '@mui/icons-material';


export default function CompletedTerminalCallList({ vessels, filteredContainers, allContainers, allTypes }) {
  const dispatch = useDispatch();
  const { data, loading, error, page, totalPages, vessel: currentVessel } =
    useSelector((state) => state.completedTerminalCalls);

  const [selectedVessel, setSelectedVessel] = useState(currentVessel);

  const formatDateKey = (date?: Date) => (date ? dayjs(date).format("DD/MM/YYYY") : "");

  const groupTerminalCalls = (calls: TerminalCall[]) =>
    calls.reduce((acc, call) => {
      const key = formatDateKey(call.planned);
      (acc[key] ||= []).push(call);
      return acc;
    }, {} as Record<string, TerminalCall[]>);

  useEffect(() => {
    dispatch(getCompletedTerminalCalls(1, selectedVessel));
  }, [dispatch, selectedVessel]);

  const handleNext = () => {
    if (!loading && (totalPages == null || page < totalPages)) {
      dispatch(getCompletedTerminalCalls(page + 1, selectedVessel));
    }
  };

  const terminalCallData = useMemo(
    () => groupTerminalCalls(data || []),
    [data]
  );
  
  return (
    <div>
      <TextField
        select
        label="Filter by vessel"
        value={selectedVessel ? selectedVessel["@id"] : ""}
        onChange={(e) => {
          const vessel = vessels.find((v) => v["@id"] === e.target.value);
          setSelectedVessel(vessel);
        }}
        sx={{ mb: 2, minWidth: 200 }}
      >
        <MenuItem value="">All vessels</MenuItem>
        {vessels.map((v) => (
          <MenuItem key={v["@id"]} value={v["@id"]}>
            {v.name}
          </MenuItem>
        ))}
      </TextField>

      {data.length === 0 && !loading && (
        <Typography>No records for that vessel.</Typography>
      )}

      <Grid item className="list-content" minWidth="40vh">
        <Grid item sx={{ maxHeight: "80vh", overflow: "auto" }}>
          {Object.entries(terminalCallData).map(([key, calls], idx) => (
            <Accordion key={key + idx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel-${idx}-content`}
                id={`panel-${idx}-header`}
              >
                <Grid
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Grid>{key}</Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                {calls.map((call) => (
                  <Grid
                    key={call["@id"]}
                    className="planning-terminal-item js-droppable-parent"
                    component={Paper}
                    data-terminal-id={call.terminal["@id"]}
                    data-terminal-call-id={call["@id"]}
                  >
                    <Grid container justifyContent="space-between">
                      { call.labels?.length > 0 ? (
                        <Grid item>{RenderLabels(call.labels)}</Grid>
                      )  : <Grid/> }
                    </Grid>
                    
                    <Grid
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1,
                        alignItems: "center",
                      }}
                    >
                      <Grid
                        sx={{
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {RenderTerminalCallLock(call)}
                        {RenderTerminalName(call.terminal)}
                      </Grid>
                      <Grid>
                        {call.planned
                          ? dayjs(call.planned).format("HH:mm")
                          : ""}
                      </Grid>
                    </Grid>
                    <Grid sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Grid container flexWrap="nowrap">
                        <Grid className="planning-terminal-action"><ContainerCounts containers={filteredContainers.filter(c => call.loadingContainers.includes(c['@id']))} containerTypes={allTypes} loading /></Grid>
                        <Grid className="planning-terminal-action"><ContainerCounts containers={filteredContainers.filter(c => call.unloadingContainers.includes(c['@id']))} containerTypes={allTypes} loading={false} /></Grid>
                      </Grid>
                      <Grid container flexWrap="nowrap">
                        <Grid className="planning-terminal-action"><TerminalCallWeightsAvailable terminalCall={call} containers={allContainers} /></Grid>
                        <Grid className="planning-terminal-action"/>
                      </Grid>
                    </Grid>     
                  </Grid>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      </Grid>

      {loading && <CircularProgress size={24} />}
      
      <Grid container xs={12} justifyContent="space-between">
          <Button 
              onClick={() => {dispatch(hideModal())}}
              variant='contained'
          >
              <CloseIcon fontSize="inherit" />&nbsp;Close
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading || (totalPages != null && page >= totalPages)}
          >
            Next
          </Button>
      </Grid>
      

      {error && <Typography color="error">{error}</Typography>}
    </div>
  );
}